import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthContextType, AuthProviderProps, CallbackLoginState, UserData, initialUserDataState } from "./interfaces/AuthContext.ts";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const initialCallbackLoginState: CallbackLoginState = {
    goBack: false,
    origin: '',
    params: {
        plan: '',
        subscription: undefined,
    },
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [phone, setPhone] = useState<string>("");
    const [token, setToken] = useState<string>(Cookies.get("jwtToken") || "");
    const [userData, setUserData] = useState<UserData>(initialUserDataState);

    const [callbackLogin, setCallbackLogin] = useState<CallbackLoginState>(initialCallbackLoginState);

    const handleLogin = (newToken: string | null): void => {
        if (newToken !== null) {
            setToken(newToken);
            Cookies.set("jwtToken", newToken, { secure: true, expires: 7 });
        }
    };

    const handleLogout = (): boolean => {
        setToken("");
        setUserData({ username: "", image: "", subscriber: "", progress: "" });
        setPhone("");
        Cookies.remove("jwtToken");
        return true;
    };

    useEffect(() => {
        if (token) {
            const decodedToken: any = jwtDecode(token);
            const { username, image, subscriber, exp, progress } = decodedToken;
            const nowTimestamp = new Date().getTime();
            const expInMilliseconds = exp * 1000
            if (nowTimestamp < expInMilliseconds) {
                setUserData({ username, image, subscriber, progress });
            } else {
                Cookies.remove("jwtToken");
            }
        } else {
            setPhone("");
        }
    }, [token]);

    const authContextValue: AuthContextType = {
        userData,
        phone,
        setPhone,
        handleLogin,
        handleLogout,
        setCallbackLogin,
        setUserData,
        callbackLogin,
        token,
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
